import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Search, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUCKS = [
  { name: "4 Tonne Truck", rate: 125 },
  { name: "6 Tonne Truck", rate: 130 },
  { name: "8 Tonne Truck", rate: 145 },
  { name: "10 Tonne Truck", rate: 160 },
  { name: "12 Tonne Truck", rate: 175 },
];

const CREW = [
  { men: 1, rate: 69 },
  { men: 2, rate: 125 },
  { men: 3, rate: 160 },
];

const inputCls =
  "w-full border border-line bg-slate-100/70 px-4 py-3 text-sm text-slate-900 placeholder-zinc-500 outline-none transition-colors focus:border-blaze";
const labelCls = "mb-1.5 block text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500";

const F = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export const ConvertQuote = ({ initialNumber }) => {
  const [num, setNum] = useState("");
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [sendConf, setSendConf] = useState(true);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = async (e, override) => {
    if (e) e.preventDefault();
    const target = (override || num).trim();
    if (!target) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/quotes/by-number/${encodeURIComponent(target)}`);
      setQuote(data);
      setForm({
        customer_name: data.customer_name || "",
        email: data.email || "",
        phone: data.phone || "",
        pickup_address: data.pickup_address || data.pickup_suburb || "",
        drop_address: data.drop_address || data.drop_suburb || "",
        moving_date: data.moving_date || "",
        preferred_time: data.preferred_time || data.time_window || "",
        bedrooms: data.bedrooms || "",
        property_type: data.property_type || data.property_size || "",
        truck_size: data.truck_size || "4 Tonne Truck",
        rate: data.rate ?? 125,
        min_hours: data.min_hours ?? 2,
        estimated_hours: data.estimated_hours ?? 3,
        booking_fee: data.booking_fee ?? "",
        callout_charge: data.callout_charge ?? "",
        discount: data.discount ?? 0,
        movers: data.movers ?? 2,
        inventory: (data.inventory || []).join(", "),
        stairs: data.stairs || "",
        special_requirements: data.special_requirements || "",
        internal_notes: data.internal_notes || "",
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Quote not found.");
      setQuote(null);
      setForm(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialNumber) {
      setNum(initialNumber);
      setResult(null);
      load(null, initialNumber);
    }
  }, [initialNumber]);

  const total =
    Math.round(
      (parseFloat(form?.rate) || 0) *
        (parseFloat(form?.estimated_hours) || 0) *
        (1 - (parseFloat(form?.discount) || 0) / 100) *
        100
    ) / 100;

  const payload = () => ({
    ...form,
    rate: parseFloat(form.rate) || 0,
    min_hours: parseFloat(form.min_hours) || 0,
    estimated_hours: parseFloat(form.estimated_hours) || 0,
    discount: parseFloat(form.discount) || 0,
    estimated_total: total,
    booking_fee: parseFloat(form.booking_fee) || 0,
    callout_charge: parseFloat(form.callout_charge) || 0,
    inventory: form.inventory.split(",").map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/quotes/${quote.id}`, payload());
      toast.success("Quote changes saved.");
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const confirm = async () => {
    setConverting(true);
    try {
      await axios.put(`${API}/quotes/${quote.id}`, payload());
      const { data } = await axios.post(`${API}/quotes/${quote.id}/convert`, { send_confirmation: sendConf });
      setResult(data);
      toast.success(`Booking ${data.booking_number} confirmed — email sent to customer.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed.");
    } finally {
      setConverting(false);
    }
  };

  if (result) {
    return (
      <div className="mt-8 border border-green-500/40 bg-green-500/[0.06] p-10 text-center" data-testid="conversion-success">
        <CheckCircle2 className="mx-auto mb-5 h-14 w-14 text-green-700" strokeWidth={1.4} />
        <h3 className="font-display text-3xl font-semibold uppercase tracking-tight text-slate-900">Booking confirmed</h3>
        <p className="mt-3 text-sm text-slate-600">
          Quote <span className="text-slate-900">{result.quote_number}</span> is now booking
        </p>
        <p className="mt-2 font-display text-5xl font-semibold tracking-tighter text-blaze" data-testid="conversion-booking-number">{result.booking_number}</p>
        <p className="mx-auto mt-4 max-w-md text-xs leading-relaxed text-slate-500">
          The customer has been emailed the confirmation with the final details. The booking
          appears under Confirmed Bookings and can be signed off on the Job Sign-Off page using this number.
        </p>
        <button
          onClick={() => { setResult(null); setQuote(null); setForm(null); setNum(""); }}
          className="mt-8 border border-line px-6 py-3 text-xs font-bold uppercase tracking-widest text-slate-700 transition-colors hover:border-blaze hover:text-blaze"
          data-testid="convert-another-btn"
        >
          Convert another quote
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="convert-quote-page">
      <form onSubmit={load} className="flex flex-wrap gap-3" data-testid="load-quote-form">
        <input
          className={`${inputCls} max-w-xs font-display text-lg tracking-wider`}
          placeholder="Quote Number — Q-202345"
          value={num}
          onChange={(e) => setNum(e.target.value)}
          data-testid="convert-quote-input"
        />
        <button type="submit" disabled={loading || !num.trim()} className="flex items-center gap-2 bg-blaze px-6 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="load-quote-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Load Quote
        </button>
      </form>
      {error && <p className="mt-4 text-sm text-blaze" data-testid="load-quote-error">{error}</p>}

      {form && quote && (
        <div className="mt-10 grid gap-8 lg:grid-cols-3">
          <div className="grid content-start gap-5 sm:grid-cols-2 lg:col-span-2" data-testid="convert-form">
            <F label="Customer Name"><input className={inputCls} value={form.customer_name} onChange={set("customer_name")} data-testid="cq-customer-name" /></F>
            <F label="Customer Email"><input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="cq-email" /></F>
            <F label="Customer Phone"><input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="cq-phone" /></F>
            <F label="Moving Date"><input type="date" className={`${inputCls} text-slate-700`} value={form.moving_date} onChange={set("moving_date")} data-testid="cq-moving-date" /></F>
            <F label="Pickup Address"><input className={inputCls} value={form.pickup_address} onChange={set("pickup_address")} data-testid="cq-pickup" /></F>
            <F label="Delivery Address"><input className={inputCls} value={form.drop_address} onChange={set("drop_address")} data-testid="cq-drop" /></F>
            <F label="Preferred Pickup Time"><input className={inputCls} placeholder="e.g. 8:00 AM" value={form.preferred_time} onChange={set("preferred_time")} data-testid="cq-time" /></F>
            <F label="Bedrooms"><input className={inputCls} placeholder="e.g. 3" value={form.bedrooms} onChange={set("bedrooms")} data-testid="cq-bedrooms" /></F>
            <F label="Property Type"><input className={inputCls} placeholder="House / Apartment…" value={form.property_type} onChange={set("property_type")} data-testid="cq-property-type" /></F>
            <F label="Truck Size">
              <select
                className={`${inputCls} appearance-none`}
                value={form.truck_size}
                onChange={(e) => {
                  const t = TRUCKS.find((x) => x.name === e.target.value);
                  setForm({ ...form, truck_size: e.target.value, rate: t ? t.rate : form.rate });
                }}
                data-testid="cq-truck"
              >
                {TRUCKS.map((t) => <option key={t.name} value={t.name} className="bg-coal text-slate-900">{t.name}</option>)}
              </select>
            </F>
            <F label="Hourly Rate ($)"><input type="number" step="0.01" className={inputCls} value={form.rate} onChange={set("rate")} data-testid="cq-rate" /></F>
            <div className="sm:col-span-2">
              <label className={labelCls}>Crew (sets rate automatically)</label>
              <div className="grid grid-cols-3 gap-3" data-testid="cq-crew-options">
                {CREW.map((c) => {
                  const active = parseInt(form.movers) === c.men;
                  return (
                    <button
                      key={c.men}
                      type="button"
                      onClick={() => setForm({ ...form, movers: c.men, rate: c.rate })}
                      data-testid={`cq-crew-${c.men}`}
                      className={`border p-4 text-center transition-all ${active ? "border-blaze bg-blaze/15" : "border-line hover:border-blaze/50"}`}
                    >
                      <p className="font-display text-lg font-semibold text-slate-900">{c.men} {c.men === 1 ? "Man" : "Men"}</p>
                      <p className="mt-1 text-sm font-semibold text-blaze">${c.rate}/hr</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <F label="Minimum Hours"><input type="number" step="0.5" className={inputCls} value={form.min_hours} onChange={set("min_hours")} data-testid="cq-min-hours" /></F>
            <F label="Estimated Hours"><input type="number" step="0.5" className={inputCls} value={form.estimated_hours} onChange={set("estimated_hours")} data-testid="cq-est-hours" /></F>
            <F label="Call-Out Charge ($)"><input type="number" min="0" step="1" placeholder="e.g. 60" className={inputCls} value={form.callout_charge} onChange={set("callout_charge")} data-testid="cq-callout" /></F>
            <F label="Booking Fee ($) — emailed with a pay link"><input type="number" min="0" step="1" placeholder="e.g. 50" className={inputCls} value={form.booking_fee} onChange={set("booking_fee")} data-testid="cq-booking-fee" /></F>
            <label className="flex items-center gap-2 pt-6 text-sm font-semibold text-slate-800">
              <input type="checkbox" checked={sendConf} onChange={(e) => setSendConf(e.target.checked)} className="h-4 w-4 accent-blue-600" data-testid="cq-send-confirmation" />
              Send confirmation email to customer (with booking fee payment link)
            </label>
            <F label="Discount (%)"><input type="number" step="1" min="0" max="100" className={inputCls} value={form.discount} onChange={set("discount")} data-testid="cq-discount" /></F>
            <div className="sm:col-span-2">
              <F label="Inventory / Items (comma separated)"><textarea rows={2} className={`${inputCls} resize-none`} value={form.inventory} onChange={set("inventory")} data-testid="cq-inventory" /></F>
            </div>
            <F label="Stairs / Access"><input className={inputCls} placeholder="e.g. 2 flights at pickup" value={form.stairs} onChange={set("stairs")} data-testid="cq-stairs" /></F>
            <F label="Special Requirements"><input className={inputCls} value={form.special_requirements} onChange={set("special_requirements")} data-testid="cq-special" /></F>
            <div className="sm:col-span-2">
              <F label="Internal Notes"><textarea rows={2} className={`${inputCls} resize-none`} value={form.internal_notes} onChange={set("internal_notes")} data-testid="cq-notes" /></F>
            </div>
          </div>

          <div className="content-start border border-blaze/50 bg-blaze/[0.05] p-7" data-testid="booking-summary">
            <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.25em] text-blaze">Booking Summary</p>
            <div className="space-y-2.5 text-sm">
              <p className="text-slate-600">Quote: <span className="font-semibold text-slate-900" data-testid="summary-quote-number">{quote.quote_number}</span></p>
              <p className="text-slate-600">Customer: <span className="font-semibold text-slate-900">{form.customer_name || "—"}</span></p>
              <p className="text-slate-600">Moving Date: <span className="font-semibold text-slate-900">{form.moving_date || "—"}</span></p>
              <p className="text-slate-600">Pickup Time: <span className="font-semibold text-slate-900">{form.preferred_time || "—"}</span></p>
              <p className="text-slate-600">Pickup: <span className="font-semibold text-slate-900">{form.pickup_address || "—"}</span></p>
              <p className="text-slate-600">Delivery: <span className="font-semibold text-slate-900">{form.drop_address || "—"}</span></p>
              <p className="text-slate-600">Truck: <span className="font-semibold text-slate-900">{form.truck_size}</span></p>
              <p className="text-slate-600">Crew: <span className="font-semibold text-slate-900">{form.movers} {parseInt(form.movers) === 1 ? "man" : "men"}</span></p>
              <p className="text-slate-600">Rate: <span className="font-semibold text-slate-900">${form.rate}/hr</span></p>
              <p className="text-slate-600">Estimated Hours: <span className="font-semibold text-slate-900">{form.estimated_hours}</span></p>
              {parseFloat(form.discount) > 0 && (
                <p className="text-slate-600">Discount: <span className="font-semibold text-blaze">{form.discount}% off</span></p>
              )}
              <div className="border-t border-blaze/30 pt-3">
                <p className="text-slate-600">Estimated Total:</p>
                <p className="font-display text-4xl font-semibold tracking-tighter text-blaze" data-testid="summary-total">${total}</p>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={save}
                disabled={saving || converting}
                className="flex w-full items-center justify-center gap-2 border border-slate-300 py-3.5 text-xs font-bold uppercase tracking-widest text-slate-900 transition-colors hover:border-blaze hover:text-blaze disabled:opacity-50"
                data-testid="save-changes-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </button>
              <button
                onClick={confirm}
                disabled={saving || converting}
                className="flex w-full items-center justify-center gap-2 bg-blaze py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                data-testid="confirm-booking-btn"
              >
                {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Booking"}
              </button>
              <p className="text-center text-[10px] leading-relaxed text-slate-500">
                The customer confirmation email is only sent when you confirm the booking.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
